import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Grid, 
  Paper,
  Typography, 
  TextField, 
  InputAdornment,
  Card, 
  CardContent, 
  CardActions,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  Divider,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { 
  Search as SearchIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Assignment as IndexIcon
} from '@mui/icons-material';
import { documentApi, Document } from '../services/api';
import api from '../services/api';
import PageHeader from '../components/PageHeader';

const DocumentList: React.FC = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [departments, setDepartments] = useState<Array<{id: number, name: string}>>([]);
  const [userDepartments, setUserDepartments] = useState<string[]>([]);
  const [userRole, setUserRole] = useState<string>('');
  const [departmentsLoading, setDepartmentsLoading] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);

  useEffect(() => {
    // 获取当前用户信息
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        console.log('用户信息:', user);
        setUserRole(user.role || '');
        setUserDepartments(user.departments || []);
        console.log('设置用户角色:', user.role);
        console.log('设置用户部门:', user.departments);
      } catch (error) {
        console.error('解析用户信息失败:', error);
      }
    }
    
    fetchDocuments();
  }, []);

  // 监听用户角色变化，如果是管理员则加载部门数据
  useEffect(() => {
    if (userRole === 'ADMIN' || userRole === 'SUPER_ADMIN') {
      console.log('用户角色为管理员，开始加载部门数据');
      fetchDepartments();
    }
  }, [userRole]);

  // 监听departmentsLoading变化
  useEffect(() => {
    console.log('departmentsLoading发生变化:', departmentsLoading);
  }, [departmentsLoading]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const data = await documentApi.getAllDocuments();
      console.log('获取到的文档数据:', data);
      setDocuments(data);
      setFilteredDocuments(data);
      
      // 提取所有唯一分类（包括部门名称和其他）
      const uniqueCategories = Array.from(
        new Set(data.map(doc => doc.category).filter(Boolean))
      );
      console.log('提取的唯一分类:', uniqueCategories);
      setCategories(uniqueCategories as string[]);
      
      setLoading(false);
    } catch (err) {
      console.error('获取文档列表失败:', err);
      setError('获取文档列表失败，请重试');
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      console.log('开始获取部门数据...');
      setDepartmentsLoading(true);
      const deptResponse = await api.get('/api/departments');
      console.log('获取到的部门数据:', deptResponse.data);
      setDepartments(deptResponse.data);
      setDepartmentsLoading(false);
      console.log('部门数据设置完成');
    } catch (deptErr) {
      console.error('加载部门列表失败:', deptErr);
      setDepartmentsLoading(false);
    }
  };

  useEffect(() => {
    let results = documents;
    
    console.log('筛选逻辑执行 - 当前状态:', {
      documentsCount: documents.length,
      searchTerm,
      selectedCategory,
      userRole,
      userDepartments
    });
    
    // 应用搜索筛选
    if (searchTerm) {
      results = results.filter(doc =>
        doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (doc.description && doc.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // 应用分类筛选
    if (selectedCategory && selectedCategory !== 'all') {
      results = results.filter(doc => doc.category === selectedCategory);
      console.log('分类筛选后文档数量:', results.length);
    }
    
    setFilteredDocuments(results);
  }, [searchTerm, selectedCategory, documents]);

  // 监听selectedCategory变化
  useEffect(() => {
    console.log('selectedCategory发生变化:', selectedCategory);
  }, [selectedCategory]);

  // 监听departments变化
  useEffect(() => {
    console.log('departments发生变化:', departments);
  }, [departments]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleCategoryChange = useCallback((event: SelectChangeEvent<string>) => {
    console.log('分类选择变化:', event.target.value);
    console.log('事件对象:', event);
    const value = event.target.value;
    console.log('设置新的分类值:', value);
    console.log('当前selectedCategory状态:', selectedCategory);
    setSelectedCategory(value);
    console.log('setSelectedCategory调用完成');
  }, []);

  const handleDeleteClick = (document: Document) => {
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (documentToDelete) {
      try {
        await documentApi.deleteDocument(documentToDelete.id);
        setDocuments(documents.filter(doc => doc.id !== documentToDelete.id));
        setFilteredDocuments(filteredDocuments.filter(doc => doc.id !== documentToDelete.id));
        setDeleteDialogOpen(false);
        setDocumentToDelete(null);
      } catch (err) {
        console.error('删除文档失败:', err);
        alert('删除文档失败，请重试');
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  const handleIndexDocument = async (documentId: number) => {
    try {
      await documentApi.markAsIndexed(documentId);
      // 更新文档状态
      setDocuments(documents.map(doc => 
        doc.id === documentId ? { ...doc, indexed: true } : doc
      ));
      alert('文档已标记为已索引');
    } catch (err) {
      console.error('标记文档为已索引失败:', err);
      alert('操作失败，请重试');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box className="page-container">
      <PageHeader
        title="文档列表"
        subtitle="浏览、筛选与管理已入库的企业文档"
        breadcrumbs={[{ label: '知识库', to: '/documents' }, { label: '文档列表' }]}
      />

      {/* 搜索和筛选工具栏 */}
      <Paper
        elevation={0}
        sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="搜索文档标题或描述..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="category-filter-label">部门分类</InputLabel>
              <Select
                labelId="category-filter-label"
                value={selectedCategory || 'all'}
                onChange={handleCategoryChange}
                label="部门分类"
                renderValue={(selected) => {
                  const v = typeof selected === 'string' ? selected : '';
                  return v === 'all' ? '所有分类' : v;
                }}
                onOpen={() => console.log('Select打开，当前值:', selectedCategory, '用户角色:', userRole, '部门数量:', departments.length, 'departmentsLoading:', departmentsLoading)}
                onClose={() => console.log('Select关闭，当前值:', selectedCategory)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300
                    }
                  }
                }}
                displayEmpty
                sx={{ 
                  '& .MuiSelect-select': {
                    cursor: 'pointer'
                  }
                }}
              >
                <MenuItem key="all" value="all">所有分类</MenuItem>
                {userRole === 'ADMIN' || userRole === 'SUPER_ADMIN' ? (
                  departmentsLoading ? (
                    <MenuItem disabled>加载中...</MenuItem>
                  ) : departments.length === 0 ? (
                    <MenuItem disabled>暂无部门数据</MenuItem>
                  ) : (
                    // 管理员和超级管理员显示所有部门，使用与普通用户相同的格式
                    departments.map((dept) => (
                      <MenuItem 
                        key={`admin-dept-${dept.name}`} 
                        value={dept.name}
                      >
                        {dept.name}
                      </MenuItem>
                    ))
                  )
                ) : (
                  // 普通用户只显示自己所属的部门
                  userDepartments.map((deptName) => (
                    <MenuItem 
                      key={`user-dept-${deptName}`} 
                      value={deptName}
                    >
                      {deptName}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<SearchIcon />}
              onClick={() => navigate('/search')}
              sx={{ height: '100%' }}
            >
              高级搜索
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* 文档列表 */}
      {filteredDocuments.length > 0 ? (
        <Grid container spacing={3}>
          {filteredDocuments.map((document) => (
            <Grid item xs={12} sm={6} md={4} key={document.id}>
              <Card className="document-card card-hover" variant="outlined">
                <CardContent className="document-card-content">
                  <Typography variant="h6" gutterBottom>
                    {document.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {document.description || '无描述'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Chip 
                      label={document.fileType} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                    {document.category && (
                      <Chip 
                        label={document.category} 
                        size="small" 
                        color="secondary" 
                        variant="outlined"
                      />
                    )}
                    <Chip 
                      label={document.indexed ? '已索引' : '未索引'} 
                      size="small" 
                      color={document.indexed ? 'success' : 'warning'} 
                    />
                  </Box>
                  <Typography variant="caption" display="block" color="text.secondary">
                    文件大小: {(document.fileSize / 1024).toFixed(2)} KB
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    上传用户: {document.uploadedBy}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    上传时间: {new Date(document.createdAt).toLocaleString()}
                  </Typography>
                </CardContent>
                <Divider />
                <CardActions className="document-card-actions">
                  <Button 
                    size="small" 
                    startIcon={<ViewIcon />}
                    onClick={() => navigate(`/documents/${document.id}`)}
                  >
                    查看
                  </Button>
                  {!document.indexed && (
                    <Button 
                      size="small" 
                      color="success"
                      startIcon={<IndexIcon />}
                      onClick={() => handleIndexDocument(document.id)}
                    >
                      索引
                    </Button>
                  )}
                  {userRole === 'SUPER_ADMIN' && (
                    <Button 
                      size="small" 
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteClick(document)}
                    >
                      删除
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            没有找到匹配的文档
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => navigate('/upload')}
          >
            上传新文档
          </Button>
        </Box>
      )}

      {/* 删除确认对话框 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            您确定要删除文档 "{documentToDelete?.title}" 吗？此操作不可撤销。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            取消
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            删除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentList; 